import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, Icon, DataWrapper} from 'components';
import {useApiData, useUserSession} from 'hooks';
import {AsyncRequestStatusName, AsyncRequestTypeName, Coordinates, ParticipantAsyncRequest} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.ACCEPT_CLIENT_GEOLOCATION_DIALOG;

type Props = {
    open: boolean;
    participantId: string;
    asyncRequest: ParticipantAsyncRequest<Coordinates>;
    fetchAsyncRequestScreenshot: (participantId: string, type: AsyncRequestTypeName) => Promise<string>;
    onClose: (data: {reset: boolean}) => void;
};

export const ParticipantGeolocationDialog: React.FC<Props> = ({
    open, participantId, asyncRequest, fetchAsyncRequestScreenshot, onClose
}: Props) => {
    const {t} = useTranslation();
    const {send: fetchGeolocationScreenshot, data: screenshot, reset} = useApiData(fetchAsyncRequestScreenshot);
    const {canManageSession} = useUserSession();
    const isSuccess = useMemo(() => asyncRequest?.status?.name === AsyncRequestStatusName.Success, [asyncRequest]);
    const [prevParticipantId, setPrevParticipantId] = useState<string>();
    const [prevScreenshotDate, setPrevScreenshotDate] = useState<Date>();
    const screenshotDate = useMemo(() => asyncRequest?.screenshot?.date, [asyncRequest]);
    const mapSrc = useMemo(() => `data:image/png;base64,${screenshot}`, [screenshot]);
    const coordinates = useMemo(() => asyncRequest?.data, [asyncRequest]);

    const handleClose = useCallback((accepted: boolean) => {
        onClose({reset: !accepted});
    }, [onClose]);

    useEffect(() => {
        if (open) {
            if ((participantId !== prevParticipantId || screenshotDate !== prevScreenshotDate)) {
                reset();
            }
            setPrevParticipantId(participantId);
            setPrevScreenshotDate(screenshotDate);
        }
    }, [open, participantId, prevParticipantId, screenshotDate, prevScreenshotDate, asyncRequest, reset]);

    useEffect(() => {
        if (open && !screenshot && isSuccess) {
            fetchGeolocationScreenshot(participantId, AsyncRequestTypeName.Geolocation);
        }
    }, [open, participantId, isSuccess, screenshot, fetchGeolocationScreenshot]);

    return (
        <ConfirmModal
            open={open}
            title={t(T_KEYS.TITLE)}
            maxWidth='sm'
            fullWidth
            okText={t(T_KEYS.DONE)}
            cancelText={t(T_KEYS.RESET)}
            hideCancelButton={!canManageSession}
            cancelBtnClassName='tw-border-alert'
            onClose={handleClose}
        >
            {asyncRequest &&
                <div className='tw-text-center tw-px-2 tw-min-h-16 md:tw-ts-h5'>
                    {isSuccess ?
                        <DataWrapper isLoading={!screenshot}>
                            {coordinates &&
                                <div className='tw-text-left tw-mb-4'>
                                    {coordinates.address && <div>{coordinates.address}</div>}
                                    <div>{`[${coordinates.latitude}, ${coordinates.longitude}]`}</div>
                                </div>}
                            {screenshot &&
                                <img className='tw-max-w-full' src={mapSrc} alt={t(T_KEYS.LOAD_IMAGE_FAILED)} />}
                        </DataWrapper> :
                        <div>
                            <Icon name='x-circle' color='alert' size='8xl' thickness='sm' className='tw-mx-auto tw-mb-4' />
                            <div className='tw-ts-h5 tw-text-alert'>
                                {t(T_KEYS.REQUEST_FAILED)}
                            </div>
                        </div>}
                </div>}
        </ConfirmModal>
    );
};
